import {
  fetchAuthSession,
  signIn as awsSignIn,
  signOut as awsSignOut,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { Observable, catchError, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { Pub, Service, Sub } from '../service.abstract.ts';
import * as Sentry from '@sentry/react';
import { storageHelper } from '../../helpers/storage/index.ts';
import { clearRecordedLoginUserIds } from '../userActivity/loginRecordSession.ts';

export enum Auth {
  loggedIn = 'loggedIn',
  unassigned = 'unassigned',
  userNotFound = 'userNotFound',
  credentialsInvalid = 'credentialsInvalid',
  credentialsEmpty = 'credentialsEmpty',
}
export enum USER_ROLES {
  ADMIN = 'admin',
  NOT_DEFINED = 'no',
  CUSTOM_ACCESS = 'custom',
}
export enum APP_MODULES {
  CUSTOMER = '1',
  ARTICLE = '2',
  OFFER = '10',
  ORDER = '3',
  PRODUCTION = '4',
  INVOICE = '5',
  DEBITOR = '6',
  STATISTIC = '9',
  OPTIONS = '7',
  GLOBAL_SEARCH = '9999', // we don't have this module in the AWS congnito, but we need it always open
}
type Actions = 'signIn' | 'signOut';

let sessionExpirationInFlight: Promise<void> | null = null;

function isExpiredAuthSessionError(error: unknown): boolean {
  const { name = '', message = '' } = error as { name?: string; message?: string };

  if (name === 'UserUnAuthenticatedException' || name === 'UserUnauthenticatedException') {
    return true;
  }

  const normalizedMessage = `${name} ${message}`.toLowerCase();
  return [
    'refresh token has expired',
    'invalid refresh token',
    'session expired',
    'no current user',
    'not authenticated',
    'user needs to be authenticated',
  ].some((phrase) => normalizedMessage.includes(phrase));
}

function expireSessionAndRedirectToLogin(): Promise<void> {
  if (sessionExpirationInFlight) return sessionExpirationInFlight;

  sessionExpirationInFlight = (async () => {
    storageHelper.session.removeItem('userAccessParams');
    storageHelper.session.removeItem('alternativeGuid');
    clearRecordedLoginUserIds();

    try {
      await awsSignOut();
    } catch {
      // The session is already considered expired; redirect is more important
      // than surfacing a best-effort Cognito sign-out failure.
    }

    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  })().finally(() => {
    queueMicrotask(() => {
      sessionExpirationInFlight = null;
    });
  });

  return sessionExpirationInFlight;
}

class PubImpl extends Pub<Actions> {
  signIn(credentials: ICredentials): void {
    this.emit('signIn', credentials);
  }
  signOut(): void {
    this.emit('signOut');
  }
}

class SubImpl extends Sub<Actions> {
  idToken(): Observable<string> {
    return from(fetchAuthSession()).pipe(
      map((session) => session.tokens?.idToken?.toString() ?? ''),
    );
  }
  /**
   * Force-refresh the Cognito session and return the fresh idToken.
   *
   * Used by the GraphQL client to recover from 401s caused by expired
   * tokens — see WA-3514 / WA-3515. `fetchAuthSession({ forceRefresh: true })`
   * exchanges the refreshToken for a new idToken bypassing Amplify's local
   * cache; the result is `''` only if the refresh token itself is dead, in
   * which case the caller should bounce the user to the login flow.
   */
  refreshIdToken(): Observable<string> {
    return from(fetchAuthSession({ forceRefresh: true })).pipe(
      map((session) => session.tokens?.idToken?.toString() ?? ''),
      catchError((error: unknown) => {
        if (isExpiredAuthSessionError(error)) {
          return of('');
        }
        return throwError(() => error);
      }),
    );
  }
  signIn(): Observable<IAuth> {
    return this.actionListener('signIn').pipe(
      switchMap(({ params }) => {
        const { username, password } = params as ICredentials;
        return from(awsSignIn({ username, password })).pipe(
          map(() => ({ status: Auth.loggedIn })),
          catchError((error: Error) => {
            switch (error.name) {
              case 'UserNotFoundException':
                return of({ status: Auth.userNotFound });
              case 'NotAuthorizedException':
                return of({ status: Auth.credentialsInvalid });
              case 'InvalidParameterException':
                return of({ status: Auth.credentialsEmpty });
              default:
                return of({ status: Auth.unassigned });
            }
          }),
        );
      }),
    );
  }
  signOut(): Observable<any> {
    return this.actionListener('signOut').pipe(
      switchMap(() => {
        storageHelper.session.removeItem('userAccessParams');
        clearRecordedLoginUserIds();
        return from(awsSignOut());
      }),
    );
  }
  isLoggedIn(): Observable<IAuth> {
    return from(fetchAuthSession()).pipe(
      map((session) => {
        if (!session.tokens?.idToken) {
          return { status: Auth.unassigned };
        }
        const payload = session.tokens.idToken.payload;
        const username = payload['cognito:username'] as string;
        const companyGuid = payload['custom:company'] as string | undefined;
        const userEmail = payload['email'] as string | undefined;

        Sentry.setUser({ email: userEmail, username });
        Sentry.setTag('company', companyGuid);

        return { status: Auth.loggedIn };
      }),
      catchError(() => of({ status: Auth.unassigned })),
    );
  }
  getCachedUserAccessParams(): Observable<IUserAccessParams> {
    const cachedData = storageHelper.session.getItem('userAccessParams');
    if (cachedData) {
      return of(cachedData as IUserAccessParams);
    }
    return this.hasAccess();
  }
  hasAccess(): Observable<IUserAccessParams> {
    return from(fetchAuthSession({ forceRefresh: true })).pipe(
      // Force refresh user session to get updated custom:access_rights and custom:access_custom_rights claims (we will send them to the backend in headers)
      switchMap(() => from(fetchUserAttributes())),
      map((attributes) => {
        const userAccessParams = structuredClone(initUserAccessParams);
        const isAdminMode = attributes?.['custom:role'] === 'admin';
        if (attributes) {
          try {
            const { faw: initRole } = JSON.parse(attributes['custom:access_rights'] as string);
            const { faw: modulesAccessRights = [] } = JSON.parse(
              attributes['custom:access_custom_rights'] as string,
            ) as { faw: string[] };

            const roleMap: Record<string, USER_ROLES> = {
              [USER_ROLES.NOT_DEFINED]: USER_ROLES.NOT_DEFINED,
              [USER_ROLES.CUSTOM_ACCESS]: USER_ROLES.CUSTOM_ACCESS,
              [USER_ROLES.ADMIN]: USER_ROLES.ADMIN,
            };
            const role = roleMap[initRole] ?? USER_ROLES.NOT_DEFINED;

            // for USER_ROLES.NOT_DEFINED we open all modules too. Because it's a default role for new users.
            if (role === USER_ROLES.NOT_DEFINED || role === USER_ROLES.ADMIN) {
              return {
                ...initUserAccessParams,
                role,
                isAdminMode,
              };
            }

            // when role === USER_ROLES.CUSTOM_ACCESS
            modulesAccessRights.forEach((moduleAccessRight) => {
              const [moduleId, isOpen] = moduleAccessRight.split(',');
              if (moduleId in userAccessParams.modules) {
                userAccessParams.modules[moduleId as APP_MODULES] = isOpen === '1';
              }
            });
            return {
              ...userAccessParams,
              role: USER_ROLES.CUSTOM_ACCESS,
              isAdminMode,
            };
          } catch (e) {
            return { ...initUserAccessParams, isAdminMode };
          }
        }
        return userAccessParams;
      }),
      tap((userAccessParams) => {
        storageHelper.session.setItem('userAccessParams', userAccessParams);
      }),
      catchError(() => of(initUserAccessParams)),
    );
  }
  customerGUID(): Observable<string> {
    return from(fetchAuthSession()).pipe(
      map((session) => {
        const guid = storageHelper.session.getItem('alternativeGuid');
        if (guid) return guid;
        return (session.tokens?.idToken?.payload?.['custom:company'] as string) ?? '';
      }),
    );
  }
  userIdentityId(): Observable<string> {
    return from(fetchAuthSession()).pipe(map((session) => session.identityId ?? ''));
  }
}
class AuthService extends Service<Actions> {
  public pub = new PubImpl(this.stream$);
  public sub = new SubImpl(this.stream$);

  expireSession(): Promise<void> {
    return expireSessionAndRedirectToLogin();
  }
}
export const authService = new AuthService();

export const initUserAccessParams: IUserAccessParams = {
  role: USER_ROLES.NOT_DEFINED,
  modules: {
    [APP_MODULES.CUSTOMER]: true,
    [APP_MODULES.ARTICLE]: true,
    [APP_MODULES.OFFER]: true,
    [APP_MODULES.ORDER]: true,
    [APP_MODULES.PRODUCTION]: true,
    [APP_MODULES.INVOICE]: true,
    [APP_MODULES.DEBITOR]: true,
    [APP_MODULES.STATISTIC]: true,
    [APP_MODULES.OPTIONS]: true,
    [APP_MODULES.GLOBAL_SEARCH]: true,
  },
  isAdminMode: false,
};

export interface ICredentials {
  username: string;
  password: string;
  alternativeguid?: string;
}

export interface IUserAccessParams {
  role: USER_ROLES;
  modules: Record<APP_MODULES, boolean>;
  isAdminMode: boolean;
}

interface IAuth {
  status: Auth;
}
